import {useMedia} from 'hooks/useMedia';
import {useAtom} from 'jotai';
import {atomWithStorage} from 'jotai/utils';
import {useCallback, useMemo} from 'react';

import {logDashboardInteraction} from './dashboardAnalytics';
import {TileId, TileSpan} from './dashboardModel';
import {LIVE_TILE_ORDER} from './liveDashboard';
import {appSlugFromTileId} from './pinnedApps';
import {getTileMeta, getTileSizes} from './tileCatalog';
import {resetCalendarTileSettings} from './tiles/calendar/calendarTileSettings';

/** Supported grid densities (columns at full width). */
export type DashboardColumns = 2 | 3 | 4;

export const DASHBOARD_COLUMN_OPTIONS: DashboardColumns[] = [2, 3, 4];

export const DEFAULT_DASHBOARD_COLUMNS: DashboardColumns = 3;

const isDashboardColumns = (value: unknown): value is DashboardColumns =>
  value === 2 || value === 3 || value === 4;

export interface DashboardLayoutConfig {
  /** Explicit sequence; tiles absent keep default relative position. */
  order: TileId[];
  /** User-removed tiles. */
  hidden: TileId[];
  /** Tiles added beyond the default visible set. */
  added: TileId[];
  /** Per-tile footprint overrides ({cols, rows}). */
  sizes: Partial<Record<TileId, TileSpan>>;
  /** Grid density preference; undefined means the default (3). */
  columns?: DashboardColumns;
}

export type DashboardLayoutStore = Record<string, DashboardLayoutConfig>;

const EMPTY_CONFIG: DashboardLayoutConfig = {
  order: [],
  hidden: [],
  added: [],
  sizes: {},
};

export const dashboardLayoutAtom = atomWithStorage<DashboardLayoutStore>('dashboard:layout', {});

const validTileIds = new Set<TileId>(LIVE_TILE_ORDER);

/** Built-in tiles plus embedded `app:<slug>` tiles. */
const isKnownTileId = (id: TileId): boolean =>
  validTileIds.has(id) || appSlugFromTileId(id) !== null;

const sanitizeTileIds = (ids: TileId[]): TileId[] =>
  ids.filter((id, index) => isKnownTileId(id) && ids.indexOf(id) === index);

/** Read a persisted `{cols, rows}` footprint; null when unrecognized. */
const coerceStoredSpan = (value: unknown): TileSpan | null => {
  if (value && typeof value === 'object') {
    const {cols, rows} = value as {cols?: unknown; rows?: unknown};
    if (typeof cols === 'number' && typeof rows === 'number' && cols > 0 && rows > 0) {
      return {cols, rows};
    }
  }
  return null;
};

const sanitizeSizes = (
  raw: Record<string, unknown> | undefined
): Partial<Record<TileId, TileSpan>> => {
  const entries = Object.entries(raw ?? {}).flatMap(([id, value]) => {
    if (!isKnownTileId(id as TileId)) {
      return [];
    }
    const span = coerceStoredSpan(value);
    return span ? [[id, span] as const] : [];
  });
  return Object.fromEntries(entries) as Partial<Record<TileId, TileSpan>>;
};

const getUserConfig = (
  store: DashboardLayoutStore,
  userId: string | undefined
): DashboardLayoutConfig => {
  if (!userId) {
    return EMPTY_CONFIG;
  }
  const raw = store[userId];
  if (!raw) {
    return EMPTY_CONFIG;
  }
  return {
    order: sanitizeTileIds(raw.order ?? []),
    hidden: sanitizeTileIds(raw.hidden ?? []),
    added: sanitizeTileIds(raw.added ?? []),
    sizes: sanitizeSizes(raw.sizes as Record<string, unknown> | undefined),
    columns: isDashboardColumns(raw.columns) ? raw.columns : undefined,
  };
};

export interface ResolvedLayout {
  order: TileId[];
  sizes: Partial<Record<TileId, TileSpan>>;
  hidden: Set<TileId>;
  added: Set<TileId>;
  /** True when the user has any persisted customization. */
  isCustomized: boolean;
}

/**
 * Merge a seed-diff layout config onto the default visible tile order.
 * `defaultOrder` comes from getVisibleLiveTilesInOrder without layout overrides.
 */
export const resolveLayout = (
  defaultOrder: TileId[],
  config: DashboardLayoutConfig | undefined
): ResolvedLayout => {
  if (!config) {
    return {
      order: defaultOrder,
      sizes: {},
      hidden: new Set(),
      added: new Set(),
      isCustomized: false,
    };
  }

  const hidden = new Set(sanitizeTileIds(config.hidden ?? []));
  const added = new Set(sanitizeTileIds(config.added ?? []));
  const orderIndex = new Map<TileId, number>();
  sanitizeTileIds(config.order ?? []).forEach((id, index) => {
    orderIndex.set(id, index);
  });

  const merged = [...defaultOrder.filter((id) => !hidden.has(id))];
  added.forEach((id) => {
    if (!hidden.has(id) && !merged.includes(id)) {
      merged.push(id);
    }
  });

  const sorted = [...merged].sort((left, right) => {
    const leftIndex = orderIndex.get(left);
    const rightIndex = orderIndex.get(right);
    if (leftIndex != null && rightIndex != null) {
      return leftIndex - rightIndex;
    }
    if (leftIndex != null) {
      return -1;
    }
    if (rightIndex != null) {
      return 1;
    }
    const leftDefault = defaultOrder.indexOf(left);
    const rightDefault = defaultOrder.indexOf(right);
    if (leftDefault >= 0 && rightDefault >= 0) {
      return leftDefault - rightDefault;
    }
    if (leftDefault >= 0) {
      return -1;
    }
    if (rightDefault >= 0) {
      return 1;
    }
    return LIVE_TILE_ORDER.indexOf(left) - LIVE_TILE_ORDER.indexOf(right);
  });

  const isCustomized =
    hidden.size > 0 ||
    added.size > 0 ||
    orderIndex.size > 0 ||
    Object.keys(config.sizes ?? {}).length > 0 ||
    config.columns != null;

  return {
    order: sorted,
    sizes: config.sizes ?? {},
    hidden,
    added,
    isCustomized,
  };
};

const spanDistance = (a: TileSpan, b: TileSpan): number =>
  Math.abs(a.cols - b.cols) + Math.abs(a.rows - b.rows);

/**
 * Resolve a tile's footprint: start from the stored override (or the tile's
 * default), then snap to the nearest curated preset so a stale or unsupported
 * size degrades gracefully to a layout the tile actually supports.
 */
export const resolveTileSpan = (
  tileId: TileId,
  sizes: Partial<Record<TileId, TileSpan>>
): TileSpan => {
  const presets = getTileSizes(tileId);
  const fallback = getTileMeta(tileId).defaultSpan;
  const requested = sizes[tileId] ?? fallback;
  const exact = presets.find((p) => p.cols === requested.cols && p.rows === requested.rows);
  if (exact) {
    return exact;
  }
  return presets.reduce(
    (best, candidate) =>
      spanDistance(candidate, requested) < spanDistance(best, requested) ? candidate : best,
    presets[0] ?? fallback
  );
};

/** Columns actually on screen; mirrors the responsive column caps in Dashboard.module.css. */
export const useEffectiveColumns = (columns: number) =>
  useMedia(['(max-width: 800px)', '(max-width: 1024px)'], [1, Math.min(columns, 2)], columns);

export const useDashboardLayout = (userId: string | undefined) => {
  const [store, setStore] = useAtom(dashboardLayoutAtom);

  const config = useMemo(() => getUserConfig(store, userId), [store, userId]);

  const updateConfig = useCallback(
    (updater: (prev: DashboardLayoutConfig) => DashboardLayoutConfig) => {
      if (!userId) {
        return;
      }
      setStore((prev) => {
        const current = getUserConfig(prev, userId);
        return {...prev, [userId]: updater(current)};
      });
    },
    [userId, setStore]
  );

  const reorder = useCallback(
    (activeId: TileId, overId: TileId, currentOrder: TileId[]) => {
      updateConfig((prev) => {
        const order = currentOrder.length > 0 ? [...currentOrder] : [...prev.order];
        const activeIndex = order.indexOf(activeId);
        const overIndex = order.indexOf(overId);
        if (activeIndex < 0 || overIndex < 0 || activeIndex === overIndex) {
          return prev;
        }
        order.splice(activeIndex, 1);
        order.splice(overIndex, 0, activeId);
        return {...prev, order};
      });
    },
    [updateConfig]
  );

  const setOrder = useCallback(
    (order: TileId[]) => {
      updateConfig((prev) => ({...prev, order: sanitizeTileIds(order)}));
    },
    [updateConfig]
  );

  const hideTile = useCallback(
    (tileId: TileId) => {
      logDashboardInteraction({action: 'tile_remove', tileId});
      updateConfig((prev) => {
        const hidden = sanitizeTileIds([...prev.hidden, tileId]);
        const added = prev.added.filter((id) => id !== tileId);
        const order = prev.order.filter((id) => id !== tileId);
        return {...prev, hidden, added, order};
      });
    },
    [updateConfig]
  );

  const addTile = useCallback(
    (tileId: TileId) => {
      if (!isKnownTileId(tileId)) {
        return;
      }
      logDashboardInteraction({action: 'tile_add', tileId});
      updateConfig((prev) => {
        const hidden = prev.hidden.filter((id) => id !== tileId);
        const added = sanitizeTileIds([...prev.added, tileId]);
        const order = sanitizeTileIds([...prev.order, tileId]);
        return {...prev, hidden, added, order};
      });
    },
    [updateConfig]
  );

  const setTileSize = useCallback(
    (tileId: TileId, span: TileSpan) => {
      logDashboardInteraction({
        action: 'tile_resize',
        tileId,
        value: `${span.cols}x${span.rows}`,
      });
      updateConfig((prev) => ({
        ...prev,
        sizes: {...prev.sizes, [tileId]: span},
      }));
    },
    [updateConfig]
  );

  const setColumns = useCallback(
    (columns: DashboardColumns) => {
      logDashboardInteraction({action: 'columns_change', value: String(columns)});
      updateConfig((prev) => ({...prev, columns}));
    },
    [updateConfig]
  );

  const resetLayout = useCallback(() => {
    if (!userId) {
      return;
    }
    logDashboardInteraction({action: 'layout_reset'});
    resetCalendarTileSettings();
    setStore((prev) => {
      const next = {...prev};
      delete next[userId];
      return next;
    });
  }, [userId, setStore]);

  return {
    config,
    reorder,
    setOrder,
    hideTile,
    addTile,
    setTileSize,
    setColumns,
    resetLayout,
  };
};
